/**
 * CSV drag-and-drop zone for the bulk-import flow.
 *
 * Reads the selected file's text content client-side and emits it via
 * `onFileSelected`. Validates size (<= 1 MB) and MIME type — the backend
 * enforces the 500-row limit and full schema validation.
 */

import React, { useCallback, useRef, useState } from 'react';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';

const MAX_SIZE_BYTES = 1024 * 1024; // 1 MB — more than enough for 500 rows.

export interface CSVDropzoneProps {
  onFileSelected: (filename: string, contents: string) => void;
  label?: string;
}

export const CSVDropzone: React.FC<CSVDropzoneProps> = ({
  onFileSelected,
  label = 'Drop a CSV file here, or click to browse',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const read = useCallback(
    (file: File) => {
      setError(null);
      if (file.size > MAX_SIZE_BYTES) {
        setError(`File is too large (${(file.size / 1024).toFixed(1)} KB). Max 1 MB.`);
        return;
      }
      if (file.type && !file.type.includes('csv') && !file.type.includes('text')) {
        setError(`Unsupported file type: ${file.type}. Provide a .csv file.`);
        return;
      }
      const reader = new FileReader();
      reader.onerror = () => setError('Failed to read file.');
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        onFileSelected(file.name, text);
      };
      reader.readAsText(file);
    },
    [onFileSelected],
  );

  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) read(file);
    // Allow re-selecting the same file after a cancel.
    event.target.value = '';
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) read(file);
  };

  return (
    <Column spacing="small">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        aria-label={label}
        style={{
          border: `2px dashed ${dragging ? '#146EB4' : '#D5DBDB'}`,
          borderRadius: '8px',
          padding: '2rem',
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: dragging ? '#E6F1FA' : '#FAFAFA',
        }}
      >
        <Text type="b300">{label}</Text>
        <Text type="b200" color="secondary">
          Expected columns: user_alias, posix_groups (semicolon-separated)
        </Text>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onFileInputChange}
        style={{ display: 'none' }}
      />
      {error && <Alert type="error">{error}</Alert>}
      <Button type="tertiary" onClick={() => inputRef.current?.click()}>
        Browse…
      </Button>
    </Column>
  );
};
